import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "@refinedev/core";

import { Grid, MenuItem, TextField } from "@mui/material";

import { fhirR4 } from "@smile-cdr/fhirts";
import { Address } from "@smile-cdr/fhirts/dist/FHIR-R4/classes/address";

const ADDRESS_USE_OPTIONS = Object.values(Address.UseEnum);

interface AddressInputProps {
  defaultValue?: fhirR4.Address;
  onChange?: (address: fhirR4.Address) => void;
}

const useAddress = (initialValue: fhirR4.Address, onChange?: (address: fhirR4.Address) => void) => {
  const [address, setAddress] = useState<fhirR4.Address>(initialValue);
  const addressRef = useRef<fhirR4.Address>(address);

  useEffect(() => {
    addressRef.current = address;
  }, [address]);

  // Function to update any field in the address
  const setAddressField = (field: keyof fhirR4.Address, value: string | string[]) => {
    const newAddress = { ...addressRef.current, [field]: value };
    setAddress(newAddress);
    if (onChange) {
      onChange(newAddress);
    }
  };

  // Function to update a specific line in the address
  const setLine = (index: number, value: string) => {
    const lines = addressRef.current.line ? [...addressRef.current.line] : [];
    while (lines.length <= index) {
      lines.push("");
    }
    lines[index] = value;
    setAddressField("line", lines);
  };

  return {
    address,
    setLine,
    setAddressField,
  };
};

const textFieldStyles = {
  // Color of the label name when in focus and not focused.
  "& .MuiInputLabel-outlined": {
    color: "text.secondary",
    "&.MuiInputLabel-shrink": {
      color: "primary.main",
    },
    "&.Mui-focused": {
      color: "text.primary",
    },
  },
};

export const AddressInput: React.FC<AddressInputProps> = ({ defaultValue = {}, onChange }) => {
  const { address, setLine, setAddressField } = useAddress(defaultValue, onChange);
  const { translate } = useTranslation();

  const handleLineChange = (e: React.ChangeEvent<HTMLInputElement>) => setLine(0, e.target.value);
  const handleLine2Change = (e: React.ChangeEvent<HTMLInputElement>) => setLine(1, e.target.value);
  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => setAddressField("city", e.target.value);
  const handleStateChange = (e: React.ChangeEvent<HTMLInputElement>) => setAddressField("state", e.target.value);
  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setAddressField("postalCode", e.target.value);
  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => setAddressField("country", e.target.value);
  const handleUseChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setAddressField("use", e.target.value as Address.UseEnum);

  const hasAddressData =
    (address.line && address.line.some(l => l.trim())) ||
    address.city?.trim() ||
    address.state?.trim() ||
    address.postalCode?.trim() ||
    address.country?.trim();
  const useRequired = !!hasAddressData && !address.use;

  return (
    <Grid container rowSpacing={3} columnSpacing={3} mt={address.use ? 2 : 0.5}>
      {/* Row 1 - Address Use, Address, Address 2 */}
      <Grid size={{ xs: 12, sm: 1.75 }}>
        <TextField
          label={translate("pages.addressInput.use")}
          variant="outlined"
          size="small"
          select
          InputProps={{ sx: { backgroundColor: "background.paper" } }}
          SelectProps={{ MenuProps: { PaperProps: { sx: { mt: 1 } } } }}
          sx={textFieldStyles}
          value={address.use || ""}
          onChange={handleUseChange}
          error={useRequired}
          helperText={useRequired ? translate("pages.addressInput.useRequired") : ""}
          fullWidth
        >
          {ADDRESS_USE_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {translate(`pages.addressInput.useOptions.${option}`)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid size={{ xs: 12, sm: 5.125 }}>
        <TextField
          label={translate("pages.addressInput.address")}
          variant="outlined"
          size="small"
          InputProps={{ sx: { backgroundColor: "background.paper" } }}
          sx={textFieldStyles}
          value={address.line && address.line[0] ? address.line[0] : ""}
          onChange={handleLineChange}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 5.125 }}>
        <TextField
          label={translate("pages.addressInput.address2")}
          variant="outlined"
          size="small"
          InputProps={{ sx: { backgroundColor: "background.paper" } }}
          sx={textFieldStyles}
          value={address.line && address.line[1] ? address.line[1] : ""}
          onChange={handleLine2Change}
          fullWidth
        />
      </Grid>

      {/* Row 2 - City, Postal/Zip, Province/State, Country */}
      <Grid size={{ xs: 12, sm: 3 }}>
        <TextField
          label={translate("pages.addressInput.city")}
          variant="outlined"
          size="small"
          InputProps={{ sx: { backgroundColor: "background.paper" } }}
          sx={textFieldStyles}
          value={address.city || ""}
          onChange={handleCityChange}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <TextField
          label={translate("pages.addressInput.postalZipCode")}
          variant="outlined"
          size="small"
          InputProps={{ sx: { backgroundColor: "background.paper" } }}
          sx={textFieldStyles}
          value={address.postalCode || ""}
          onChange={handlePostalCodeChange}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <TextField
          label={translate("pages.addressInput.provinceState")}
          variant="outlined"
          size="small"
          InputProps={{ sx: { backgroundColor: "background.paper" } }}
          sx={textFieldStyles}
          value={address.state || ""}
          onChange={handleStateChange}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <TextField
          label={translate("pages.addressInput.country")}
          variant="outlined"
          size="small"
          InputProps={{ sx: { backgroundColor: "background.paper" } }}
          sx={textFieldStyles}
          value={address.country || ""}
          onChange={handleCountryChange}
          fullWidth
        />
      </Grid>
    </Grid>
  );
};
